import { urlSchema } from '@kanbu/schema/contracts';
import type { UpdateFrequency } from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, cn, Input, Label, Select, SelectItem, toast } from '@utima/ui';
import { Info, RefreshCw, Rss } from 'lucide-react';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import { LockedFeatureIcon } from '@/components';
import { Alert } from '@/components/Alert';
import { SimpleTooltip } from '@/components/tooltips/simple-tooltip';
import { useEnumTranslations } from '@/hooks';
import { useSubscriptionFeatures } from '@/hooks/useSubscriptionFeatures';
import { invalidateKB } from '@/services/invalidateKB';
import { trpc } from '@/services/trpc';

import type { AddSourcePanelHandle } from './addSourcePanelHandle';
import { UPDATE_FREQUENCY_OPTIONS } from '../../lib/updateFrequencyOptions';

/** Local option: let the backend heuristic decide the content mode. */
const AUTO_CONTENT_MODE = '__auto__' as const;
const INHERIT_UPDATE_FREQUENCY = '__inherit__' as const;

type ContentModeOverride = typeof AUTO_CONTENT_MODE | 'inline' | 'follow_link';

export type AddRssFeedProps = {
  chatIds: string[];
  availableForAllAgents: boolean;
  setOpen: (open: boolean) => void;
  defaultUrl?: string;
  limitReached: boolean;
  projectedLimitReached: boolean;
  folderId?: string | null;
  /** Renders as a settings-only panel: no own URL input and no own
   * Add/submit button — the shared "Online sources" row owns the URL and
   * drives submission via the imperative handle (Phase R6). */
  embedded?: boolean;
  onSubmittingChange?: (isSubmitting: boolean) => void;
};

/**
 * RSS/Atom feed add-form, reached from the "Online sources" tab's URL
 * classifier (master K4b) once a URL is detected (or manually overridden) as
 * a feed: URL → settings (content mode, auto refresh) → create. The create
 * mutation resolves the feed server-side, so there is no client-side test
 * step (Phase R6).
 */
export const AddRssFeed = memo(
  forwardRef<AddSourcePanelHandle, AddRssFeedProps>(function AddRssFeedForm(
    {
      chatIds,
      availableForAllAgents,
      setOpen,
      defaultUrl,
      limitReached,
      projectedLimitReached,
      folderId,
      embedded = false,
      onSubmittingChange,
    },
    ref,
  ) {
    const { t } = useLingui();
    const enumTranslations = useEnumTranslations();
    const { isKbAutoUpdatesLocked } = useSubscriptionFeatures();

    const [url, setUrl] = useState(defaultUrl ?? '');
    const [contentModeOverride, setContentModeOverride] =
      useState<ContentModeOverride>(AUTO_CONTENT_MODE);
    const [updateFrequency, setUpdateFrequency] =
      useState<UpdateFrequency | null>(null);

    const { mutateAsync: createRss, isPending: isCreating } =
      trpc.documentFolders.createRss.useMutation({
        onSuccess: invalidateKB,
      });

    useEffect(() => {
      if (defaultUrl) {
        setUrl(defaultUrl);
      }
    }, [defaultUrl]);

    const isValidUrl = urlSchema.safeParse(url).success;

    const handleCreate = useCallback(async () => {
      if (!isValidUrl) {
        toast.error(t`Enter a valid feed URL first.`);

        return;
      }

      if (limitReached || projectedLimitReached) {
        toast.error(t`Cannot add more documents for your plan.`);

        return;
      }

      try {
        await createRss({
          url,
          parentId: folderId ?? null,
          chatIds,
          availableForAllAgents,
          ...(contentModeOverride === AUTO_CONTENT_MODE
            ? {}
            : { contentMode: contentModeOverride }),
          ...(updateFrequency == null ? {} : { updateFrequency }),
        });

        toast.success(t`Feed added. Items will appear as they are synced.`);
        setOpen(false);
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : t`Failed to add the feed.`,
        );
      }
    }, [
      availableForAllAgents,
      chatIds,
      contentModeOverride,
      createRss,
      folderId,
      isValidUrl,
      limitReached,
      projectedLimitReached,
      setOpen,
      t,
      updateFrequency,
      url,
    ]);

    useImperativeHandle(ref, () => ({ submit: handleCreate }), [handleCreate]);

    useEffect(() => {
      onSubmittingChange?.(isCreating);
    }, [isCreating, onSubmittingChange]);

    return (
      <>
        {limitReached && (
          <Alert
            variant='warning'
            title={t`Document Limit Reached`}
            className='mb-4'
          >
            <Trans>
              You have reached the maximum document limit for your plan.
            </Trans>
          </Alert>
        )}

        <div className='space-y-2'>
          {!embedded && (
            <div className='flex flex-col gap-2 sm:flex-row sm:items-end'>
              <div className='min-w-0 flex-1 space-y-1'>
                <Label htmlFor='rss-url'>{t`Feed URL`}:</Label>
                <Input
                  id='rss-url'
                  name='rss-url'
                  data-cta='admin-add-rss-url-input'
                  placeholder='https://example.com/rss'
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  required
                />
              </div>
              <Button
                data-cta='admin-add-rss-create-button'
                disabled={
                  isCreating ||
                  !isValidUrl ||
                  limitReached ||
                  projectedLimitReached
                }
                loading={isCreating}
                onClick={handleCreate}
                icon={<Rss className={cn('size-4')} />}
              >
                <Trans>Add feed</Trans>
              </Button>
            </div>
          )}

          <details className='shrink-0 rounded-lg bg-muted/30 px-3 py-2'>
            <summary className='cursor-pointer text-xs text-muted-fg'>
              <Trans>Advanced settings</Trans>
            </summary>
            <div className='mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2'>
              <div className='min-w-0'>
                <div className='mb-2 flex items-center gap-2'>
                  <Label className='text-xs font-medium'>
                    {t`Content mode`}:
                  </Label>
                  <SimpleTooltip
                    title={t`Auto-detect lets Kanbu pick per item. Inline uses the content embedded in the feed. Follow link scrapes the linked page for the full article.`}
                  >
                    <Info className='size-3 shrink-0 cursor-help text-muted-fg' />
                  </SimpleTooltip>
                </div>
                <Select
                  value={contentModeOverride}
                  onValueChange={v =>
                    setContentModeOverride(v as ContentModeOverride)
                  }
                >
                  <SelectItem value={AUTO_CONTENT_MODE}>
                    <Trans>Auto-detect</Trans>
                  </SelectItem>
                  <SelectItem value='inline'>
                    <Trans>Inline (use feed content)</Trans>
                  </SelectItem>
                  <SelectItem value='follow_link'>
                    <Trans>Follow link (scrape the page)</Trans>
                  </SelectItem>
                </Select>
              </div>

              <div
                className={cn('min-w-0', isKbAutoUpdatesLocked && 'opacity-50')}
              >
                <div className='mb-2 flex items-center gap-2'>
                  <RefreshCw className='size-3.5 shrink-0 text-muted-fg' />
                  <Label className='text-xs font-medium'>
                    <Trans>Auto Refresh</Trans>
                  </Label>
                  <LockedFeatureIcon locked={isKbAutoUpdatesLocked} />
                  <SimpleTooltip
                    title={t`How often the feed is re-checked for new items.`}
                  >
                    <Info className='size-3 shrink-0 cursor-help text-muted-fg' />
                  </SimpleTooltip>
                </div>
                <div
                  className={cn(isKbAutoUpdatesLocked && 'pointer-events-none')}
                >
                  <Select
                    value={updateFrequency ?? INHERIT_UPDATE_FREQUENCY}
                    disabled={isKbAutoUpdatesLocked}
                    onValueChange={v =>
                      setUpdateFrequency(
                        v === INHERIT_UPDATE_FREQUENCY
                          ? null
                          : (v as UpdateFrequency),
                      )
                    }
                  >
                    <SelectItem value={INHERIT_UPDATE_FREQUENCY}>
                      <Trans>Inherit</Trans>
                    </SelectItem>
                    {UPDATE_FREQUENCY_OPTIONS.map(freq => (
                      <SelectItem key={freq} value={freq}>
                        {enumTranslations.updateFrequency[freq]}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
          </details>
        </div>
      </>
    );
  }),
);
